
import Link from 'next/link'
import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Facebook,
  Linkedin,
  User,
  FileText,
  Building,
  Scale
} from 'lucide-react'

export default function Footer() {
  const currentYear = new Date().getFullYear()

  const quickLinks = [
    { title: 'Home', href: '/' },
    { title: 'About Us', href: '/about' },
    { title: 'Our Team', href: '/about/team' },
    { title: 'Contact Us', href: '/contact' },
  ]

  const services = [
    { title: 'Immigration & Visa Services', href: '/services/immigration', icon: User },
    { title: 'Notary Public Services', href: '/services/notary-public', icon: FileText },
    { title: 'Business Law', href: '/services/business-law', icon: Building },
    { title: 'Family Law', href: '/services/family-law', icon: Scale },
  ]

  const legalLinks = [
    { title: 'Terms of Use', href: '/legal/terms' },
    { title: 'Privacy Policy', href: '/legal/privacy' },
    { title: 'Anti-spam Policy', href: '/legal/anti-spam' },
  ]

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="relative w-48 h-12">
              <Image
                src="https://cdn.abacus.ai/images/74600ebe-f729-4c6a-bebd-0217a333d059.png"
                alt="Visatec Legal Logo"
                fill
                className="object-contain brightness-0 invert"
              />
            </div>
            <div className="space-y-4">
              <p className="text-gray-300 text-sm leading-relaxed">
                Professional immigration and legal services in Melbourne with over 20 years of experience. 
                We help individuals and businesses navigate Australian visa applications and legal matters.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-gray-300">
                    <div>Level 1, 1 Queens Road</div>
                    <div>Melbourne, VIC 3004</div>
                    <div>Australia</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <div className="text-sm text-gray-300">
                    <div>1300 484 728</div>
                    <div>+61 483 907 326</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <a href="mailto:info@visatec.com.au" className="text-sm text-gray-300 hover:text-white transition-colors">
                    info@visatec.com.au
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <div className="text-sm text-gray-300">
                    Mon-Fri: 9:00 AM - 6:00 PM
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks?.map((link) => (
                <li key={link?.title}>
                  <Link 
                    href={link?.href || '#'} 
                    className="text-gray-300 hover:text-white transition-colors text-sm inline-flex items-center group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform duration-200">
                      {link?.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Social Links */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-white">Connect With Us</h4>
              <div className="flex gap-4">
                <Link 
                  href="https://facebook.com/visateclawyersandnotarypublic" 
                  target="_blank"
                  className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </Link>
                <Link 
                  href="https://linkedin.com/company/visatec-legal" 
                  target="_blank"
                  className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">Our Services</h3>
            <ul className="space-y-3">
              {services?.map((service) => (
                <li key={service?.title}>
                  <Link 
                    href={service?.href || '#'} 
                    className="text-gray-300 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                  >
                    <service.icon className="w-4 h-4 text-blue-400" />
                    <span className="group-hover:translate-x-1 transition-transform duration-200">
                      {service?.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact CTA */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">Get Started Today</h3>
            <div className="space-y-4">
              <p className="text-gray-300 text-sm">
                Ready to discuss your legal needs? Contact us for a free consultation.
              </p>
              <div className="space-y-3">
                <Button asChild className="w-full bg-blue-600 hover:bg-blue-700">
                  <Link href="/contact">Free Consultation</Link>
                </Button>
                <Button asChild variant="outline" className="w-full border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white">
                  <Link href="/get-started">Get Started Online</Link>
                </Button>
              </div>
            </div>

            {/* Professional Accreditations */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-white">Accreditations</h4>
              <div className="text-xs text-gray-400 space-y-1">
                <div>• Migration Institute of Australia</div>
                <div>• Law Institute of Victoria</div>
                <div>• MARA Registration</div>
                <div>• Notary Public Victoria</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-gray-400 text-sm text-center sm:text-left">
              © {currentYear} Visatec Legal. All rights reserved.
            </div>
            <div className="flex flex-wrap gap-4 text-xs">
              {legalLinks?.map((link, index) => (
                <span key={link?.title} className="flex items-center gap-4">
                  <Link 
                    href={link?.href || '#'} 
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {link?.title}
                  </Link>
                  {index < legalLinks.length - 1 && (
                    <span className="text-gray-600">|</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
